import { useEffect, useState } from 'react';
import api from '../services/api';
import MathText from '../components/MathText';
import SEO from '../components/SEO';
import ThemeSwitcher from '../components/ThemeSwitcher';

const QuestionPage = ({ examId, questionId, onBack, onEditExam, user, onGoToBrowse, theme, onThemeChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Editing state
  const [editingSolution, setEditingSolution] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [solutionInput, setSolutionInput] = useState('');
  const [notesInput, setNotesInput] = useState('');
  const [saving, setSaving] = useState(false);

  // Mobile layout state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 720);

  const { question, examTitle, prevQuestionId, nextQuestionId, currentIndex, totalQuestions, createdBy, authorizedEditors } = data || {};

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 720);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setLoading(true);
    setEditingSolution(false);
    setEditingNotes(false);
    api.get(`/exams/${examId}/questions/${questionId}`)
      .then(res => {
        setData(res.data);
        setSolutionInput(res.data.question.solution || '');
        setNotesInput(res.data.question.notes || '');
        setLoading(false);
      })
      .catch(err => {
        setError(err.response?.data?.error || 'Failed to load question details');
        setLoading(false);
      });
  }, [examId, questionId]);

  const handleNavigate = (targetId) => {
    if (!targetId) return;
    const newPath = `/question/${examId}/${targetId}`;
    window.history.pushState({}, '', newPath);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (document.activeElement?.tagName === 'TEXTAREA' || document.activeElement?.tagName === 'INPUT') {
        return;
      }
      if (e.key === 'ArrowLeft' && prevQuestionId) {
        handleNavigate(prevQuestionId);
      } else if (e.key === 'ArrowRight' && nextQuestionId) {
        handleNavigate(nextQuestionId);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [prevQuestionId, nextQuestionId, examId]);

  const handleSave = async (field, value) => {
    setSaving(true);
    try {
      const res = await api.put(`/exams/${examId}/questions/${questionId}`, {
        [field]: value
      });
      setData(prev => ({
        ...prev,
        question: res.data.question
      }));
      if (field === 'solution') setEditingSolution(false);
      if (field === 'notes') setEditingNotes(false);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: '16px' }}>
        <div className="page-card" style={{ textAlign: 'center', padding: '60px', maxWidth: '500px' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ color: 'var(--text-muted)', marginTop: '16px' }}>Loading question details…</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh', padding: '16px' }}>
        <div className="page-card" style={{ maxWidth: '500px', width: '100%' }}>
          <button className="secondary" onClick={onGoToBrowse}>← Home</button>
          <div className="error-message" style={{ marginTop: '16px' }}>{error || 'Question not found'}</div>
        </div>
      </div>
    );
  }

  const isMcq = question.type === 'mcq';
  const options = (question.options || []).filter(o => o?.trim());
  const correctAnswers = question.correctAnswers || [];

  // Determine if current user can edit this exam
  const isStaff = user && ['super_user', 'college_admin', 'teacher'].includes(user.role);
  const isCreator = user && createdBy && (createdBy._id || createdBy)?.toString() === user._id?.toString();
  const isAuthorizedEditor = user && Array.isArray(authorizedEditors) && authorizedEditors.some(id => id?.toString() === user._id?.toString());
  const canEdit = isStaff || isCreator || isAuthorizedEditor;
  const isGuest = !user || user.role === 'guest';

  // Generate clean SEO tags
  const cleanText = question.text.replace(/\\/g, '').replace(/[\$\{\}]/g, '');
  const seoTitle = `Question: ${cleanText.length > 55 ? cleanText.substring(0, 55) + '...' : cleanText}`;
  const seoDescription = `Solve the question: "${cleanText}". View correct answer choices, detailed explanation, and study notes on Study Centro.`;

  return (
    <div className="test-page-container header-visible" style={{ margin: '0 auto', minHeight: '100vh', overflowY: 'auto' }}>
      <SEO title={seoTitle} description={seoDescription} />
      
      {/* ── Header Shell ── */}
      <header className="test-header">
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          padding: isMobile ? '8px 12px' : '12px 32px', 
          width: '100%',
          boxSizing: 'border-box'
        }}>
          {/* Left side: Edit Paper button (admins/editors only) & Exam title */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {canEdit && onEditExam && (
              <button 
                className="secondary submit-btn-small" 
                onClick={onEditExam}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}
                title="Back to Edit Paper"
              >
                <i className="fas fa-arrow-left" /> {!isMobile && 'Edit Paper'}
              </button>
            )}
            {!isMobile && (
              <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginLeft: canEdit ? '6px' : '0' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Exam:</span>
                <strong style={{ fontSize: '0.95rem', maxWidth: '240px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <MathText text={examTitle} />
                </strong>
              </div>
            )}
          </div>

          {/* Header Navigation Center */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              className="secondary submit-btn-small" 
              onClick={() => handleNavigate(prevQuestionId)}
              disabled={!prevQuestionId}
              style={{ padding: '8px 10px', opacity: prevQuestionId ? 1 : 0.5, cursor: prevQuestionId ? 'pointer' : 'default' }}
              title="Previous Question"
            >
              <i className="fas fa-chevron-left" />
            </button>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: '600', whiteSpace: 'nowrap' }}>
              Q{currentIndex + 1} of {totalQuestions}
            </span>
            <button 
              className="secondary submit-btn-small" 
              onClick={() => handleNavigate(nextQuestionId)}
              disabled={!nextQuestionId}
              style={{ padding: '8px 10px', opacity: nextQuestionId ? 1 : 0.5, cursor: nextQuestionId ? 'pointer' : 'default' }}
              title="Next Question"
            >
              <i className="fas fa-chevron-right" />
            </button>
          </div>

          {/* Right side: Home button & Theme switcher */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              className="secondary submit-btn-small" 
              onClick={onGoToBrowse}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px' }}
              title="Go to Home"
            >
              <i className="fas fa-home" /> {!isMobile && 'Home'}
            </button>
            <ThemeSwitcher 
              theme={theme} 
              onThemeChange={onThemeChange} 
              compact={true} 
              showLabel={false} 
            />
          </div>
        </div>
      </header>

      {/* ── Main Content Body ── */}
      <div className="test-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        
        {/* Responsive padding wrapper to bypass App.css's test-body padding overrides */}
        <div style={{ 
          width: '100%', 
          maxWidth: '900px', 
          padding: isMobile ? '12px 16px 80px' : '20px 16px 80px',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          gap: '24px'
        }}>
          
          <div className="page-card" style={{ width: '100%', boxSizing: 'border-box' }}>
            
            {/* Header: Exam info (mobile only) & Question text */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: '1px solid var(--border-light)', paddingBottom: '20px', marginBottom: '20px' }}>
              {isMobile && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  <i className="fas fa-book-open" style={{ marginRight: '6px' }} />
                  Exam: <MathText text={examTitle} />
                </div>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                {question.type && (
                  <span style={{ background: 'var(--bg-main)', color: 'var(--text-muted)', border: '1px solid var(--border-light)', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: '600', letterSpacing: '0.5px' }}>
                    {question.type === 'mcq' ? 'Multiple Choice' : 'Open Answer'}
                  </span>
                )}
              </div>
              
              <h2 style={{ fontSize: isMobile ? '1.25rem' : '1.5rem', marginTop: '8px', marginBottom: '0', fontWeight: '600', color: 'var(--text-main)', lineHeight: '1.4' }}>
                <span style={{ color: 'var(--primary)', marginRight: '8px' }}>Q{currentIndex + 1}.</span>
                <MathText text={question.text} />
              </h2>
              {question.imageUrl && (
                <div style={{ marginTop: '16px', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                  <img 
                    src={question.imageUrl} 
                    alt="Question" 
                    style={{ maxWidth: '100%', maxHeight: '400px', borderRadius: '12px', border: '1px solid var(--border-light)', boxShadow: 'var(--shadow-sm)', cursor: 'zoom-in' }} 
                    onClick={() => window.open(question.imageUrl, '_blank')}
                  />
                </div>
              )}
            </div>

            {/* Answer Options */}
            <div style={{ marginBottom: '24px' }}>
              {isMcq ? (
                <ul style={{ listStyleType: 'none', padding: 0, display: 'grid', gap: '12px', marginBottom: '0' }}>
                  {question.options.map((opt, i) => {
                    const optImg = question.optionImages?.[i];
                    const optIdentifier = opt?.trim() || optImg;
                    if (!optIdentifier) return null;
                    const isCorrect = correctAnswers.includes(optIdentifier);
                    const borderColor = isCorrect ? 'var(--success)' : 'var(--border-light)';
                    const bg = isCorrect ? 'rgba(16, 185, 129, 0.05)' : 'var(--bg-main)';
                    const textColor = isCorrect ? 'var(--success)' : 'var(--text-main)';
                    const iconColor = isCorrect ? 'var(--success)' : 'var(--danger)';
                    const iconBg = isCorrect ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)';
                    const icon = isCorrect ? 'fa-check' : 'fa-times';

                    return (
                      <li 
                        key={i} 
                        className="test-option"
                        style={{ 
                          borderColor: borderColor,
                          background: bg,
                          cursor: 'default', 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '12px',
                          padding: '14px 16px',
                          borderRadius: '12px',
                          color: textColor,
                          borderWidth: '2px',
                          borderStyle: 'solid',
                          pointerEvents: 'none'
                        }}
                      >
                        <div style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '50%',
                          background: iconBg,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: iconColor,
                          flexShrink: 0,
                          fontSize: '0.9rem'
                        }}>
                          <i className={`fas ${icon}`} />
                        </div>
                        <div className="test-option-text" style={{ color: textColor, fontWeight: isCorrect ? '600' : 'normal', flex: 1, fontSize: '1.05rem', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {opt?.trim() && <MathText text={opt} />}
                          {optImg && (
                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px', pointerEvents: 'auto' }}>
                              <img 
                                src={optImg} 
                                alt={`Option ${i + 1}`} 
                                style={{ maxWidth: '250px', maxHeight: '180px', borderRadius: '8px', border: '1px solid var(--border-light)', cursor: 'zoom-in' }} 
                                onClick={() => window.open(optImg, '_blank')}
                              />
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div style={{ background: 'var(--bg-main)', borderRadius: '12px', padding: '20px', border: '1px solid var(--border-light)' }}>
                  <strong style={{ color: 'var(--success)', display: 'block', marginBottom: '10px', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Correct Answer(s):</strong>
                  <ul style={{ margin: '4px 0 0', paddingLeft: '20px', color: 'var(--success)', fontSize: '1.05rem', lineHeight: '1.5' }}>
                    {correctAnswers.map((a, i) => (
                      <li key={i}>
                        {typeof a === 'string' && (a.startsWith('http://') || a.startsWith('https://') || a.startsWith('/')) ? (
                          <img src={a} alt="Correct Choice" style={{ maxHeight: '100px', borderRadius: '4px', border: '1px solid var(--border-light)', display: 'block', marginTop: '4px' }} />
                        ) : (
                          <MathText text={a} />
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Solution & Explanation Section */}
            <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '24px', marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-main)', fontWeight: '600' }}>
                  <i className="fas fa-lightbulb" style={{ color: 'var(--warning)' }} /> Solution & Explanation
                </h3>
                {!isGuest && !editingSolution && (
                  <button className="secondary submit-btn-small" onClick={() => setEditingSolution(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-edit" /> Edit
                  </button>
                )}
              </div>

              {editingSolution ? (
                <div style={{ display: 'grid', gap: '16px' }}>
                  <textarea
                    className="test-open-answer"
                    value={solutionInput}
                    onChange={(e) => setSolutionInput(e.target.value)}
                    placeholder="Provide a step-by-step solution/explanation for this question..."
                    rows={6}
                    style={{ minHeight: '120px', padding: '16px', borderRadius: '12px', border: '2px solid var(--border-light)', background: 'var(--bg-main)', color: 'var(--text-main)' }}
                  />
                  {solutionInput.trim() && (
                    <div style={{ padding: '16px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Live Preview</div>
                      <div style={{ color: 'var(--text-main)', fontSize: '1.05rem', lineHeight: '1.6' }}>
                        <MathText text={solutionInput} />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button className="primary submit-btn-small" onClick={() => handleSave('solution', solutionInput)} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="secondary submit-btn-small" onClick={() => { setSolutionInput(question.solution || ''); setEditingSolution(false); }} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  background: 'var(--bg-main)', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  border: '1px solid var(--border-light)', 
                  color: 'var(--text-main)', 
                  fontSize: '1.05rem', 
                  lineHeight: '1.7', 
                  whiteSpace: 'pre-wrap' 
                }}>
                  {question.solution ? (
                    <MathText text={question.solution} />
                  ) : (
                    <em style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>No explanation available yet.</em>
                  )}
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div style={{ borderTop: '1px solid var(--border-light)', paddingTop: '24px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--text-main)', fontWeight: '600' }}>
                  <i className="fas fa-sticky-note" style={{ color: 'var(--primary)' }} /> Study Notes
                </h3>
                {!isGuest && !editingNotes && (
                  <button className="secondary submit-btn-small" onClick={() => setEditingNotes(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <i className="fas fa-edit" /> Edit
                  </button>
                )}
              </div>

              {editingNotes ? (
                <div style={{ display: 'grid', gap: '16px' }}>
                  <textarea
                    className="test-open-answer"
                    value={notesInput}
                    onChange={(e) => setNotesInput(e.target.value)}
                    placeholder="Add your own reference notes, formulas, or key reminders..."
                    rows={6}
                    style={{ minHeight: '120px', padding: '16px', borderRadius: '12px', border: '2px solid var(--border-light)', background: 'var(--bg-main)', color: 'var(--text-main)' }}
                  />
                  {notesInput.trim() && (
                    <div style={{ padding: '16px', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border-light)' }}>
                      <div style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>Live Preview</div>
                      <div style={{ color: 'var(--text-main)', fontSize: '1.05rem', lineHeight: '1.6' }}>
                        <MathText text={notesInput} />
                      </div>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button className="primary submit-btn-small" onClick={() => handleSave('notes', notesInput)} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button className="secondary submit-btn-small" onClick={() => { setNotesInput(question.notes || ''); setEditingNotes(false); }} disabled={saving}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ 
                  background: 'var(--bg-main)', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  border: '1px solid var(--border-light)', 
                  color: 'var(--text-main)', 
                  fontSize: '1.05rem', 
                  lineHeight: '1.7', 
                  whiteSpace: 'pre-wrap' 
                }}>
                  {question.notes ? (
                    <MathText text={question.notes} />
                  ) : (
                    <em style={{ color: 'var(--text-muted)', fontSize: '0.95rem' }}>No notes added yet.</em>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Navigation */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', padding: '24px 0 0', borderTop: '1px solid var(--border-light)' }}>
              <button 
                className="secondary submit-btn-small" 
                onClick={() => handleNavigate(prevQuestionId)}
                disabled={!prevQuestionId}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', fontSize: '0.95rem', opacity: prevQuestionId ? 1 : 0.5, cursor: prevQuestionId ? 'pointer' : 'default' }}
              >
                <i className="fas fa-chevron-left" /> {isMobile ? 'Prev' : 'Previous Question'}
              </button>
              <span style={{ fontSize: '0.9rem', color: 'var(--text-muted)', fontWeight: '600' }}>
                Q{currentIndex + 1} / {totalQuestions}
              </span>
              <button 
                className="secondary submit-btn-small" 
                onClick={() => handleNavigate(nextQuestionId)}
                disabled={!nextQuestionId}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', fontSize: '0.95rem', opacity: nextQuestionId ? 1 : 0.5, cursor: nextQuestionId ? 'pointer' : 'default' }}
              >
                {isMobile ? 'Next' : 'Next Question'} <i className="fas fa-chevron-right" />
              </button>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default QuestionPage;
